import React, { useMemo, useState } from "react"
import { faCalendar, faClose, faMoneyBill, faPenToSquare, faPlus } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { ButtonIcon } from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import { GenericDropDown } from "../../components/Dropdown/Dropdown"
import { SimpleToggle } from "../../components/SimpleToggle/SimpleToggle"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { EBillingPeriodType } from "../../Enum/EBillingPeriodType"
import { AdminPlan } from "../../Objects/AdminPlan/AdminPlan"
import { AdminPlanPeriod } from "../../Objects/AdminPlan/AdminPlanPeriod"
import { AdminCreatePlanPeriod } from "../../Objects/AdminPlan/AdminCreatePlanPeriod"
import { AdminUpdatePlanPeriod } from "../../Objects/AdminPlan/AdminUpdatePlanPeriod"

interface AdminPlanPeriodPopupProps {
  plan: AdminPlan
  period: AdminPlanPeriod | null
  onClose: () => void
  onCreate: (planId: number, request: AdminCreatePlanPeriod) => void
  onUpdate: (planPeriodId: number, request: AdminUpdatePlanPeriod) => void
}

const toMoneyNumber = (value: string) => {
  const normalized = value.replace(",", ".").trim()

  const parsed = normalized ? Number.parseFloat(normalized) : 0

  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0
}

export const AdminPlanPeriodPopup: React.FC<AdminPlanPeriodPopupProps> = ({
  plan,
  period,
  onClose,
  onCreate,
  onUpdate,
}) => {
  const { t } = useTranslation("frontend")

  const isEditing = period !== null

  const availableBillingPeriodOptions = useMemo(() => {
    const usedTypes = new Set(plan.periods.map((item) => item.eBillingPeriodType))

    return Object.values(EBillingPeriodType)
      .filter((value): value is EBillingPeriodType => typeof value === "number")
      .filter((value) => isEditing || !usedTypes.has(value))
      .map((value) => ({ value: String(value), label: (value as number).toEBillingPeriodTypeToString() }))
  }, [plan.periods, isEditing])

  const [billingPeriodType, setBillingPeriodType] = useState<EBillingPeriodType>(
    period?.eBillingPeriodType ??
      (Number(availableBillingPeriodOptions[0]?.value ?? EBillingPeriodType.Monthly) as EBillingPeriodType),
  )

  const [price, setPrice] = useState(String(period?.price ?? ""))

  const [isActive, setIsActive] = useState(period?.isActive ?? true)

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (isEditing) {
      onUpdate(period.id, new AdminUpdatePlanPeriod({ price: toMoneyNumber(price), isActive }))

      return
    }

    onCreate(plan.id, new AdminCreatePlanPeriod({ eBillingPeriodType: billingPeriodType, price: toMoneyNumber(price) }))
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={
                isEditing
                  ? t("admin_dashboard.plans_and_coupons.period_edit_title")
                  : t("admin_dashboard.plans_and_coupons.period_create_title")
              }
              icon={isEditing ? faPenToSquare : faPlus}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanPeriodPopup.button.onClose"
                />
              }
            />

            <form onSubmit={handleSubmit} className="mt-3 space-y-3">
              <p className="rounded-lg border border-orange-100 bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-800">
                {plan.name}
              </p>

              <div className="grid grid-cols-1 gap-3 p-2">
                {isEditing ? (
                  <InputField
                    type="text"
                    disable
                    placeholder={t("admin_dashboard.plans_and_coupons.period_billing_type")}
                    icon={faCalendar}
                    value={period.eBillingPeriodType.toEBillingPeriodTypeToString()}
                    onChange={() => undefined}
                    descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanPeriodPopup.input.billingPeriodType"
                  />
                ) : (
                  <GenericDropDown
                    data={availableBillingPeriodOptions}
                    initialState={String(billingPeriodType)}
                    handlerFuncion={(event) => setBillingPeriodType(Number(event.target.value) as EBillingPeriodType)}
                    descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanPeriodPopup.select.billingPeriodType"
                  />
                )}

                <InputField
                  type="number"
                  placeholder={t("admin_dashboard.plans_and_coupons.period_price")}
                  onInvalidMessage={t("admin_dashboard.plans_and_coupons.period_price_invalid")}
                  icon={faMoneyBill}
                  value={price}
                  required
                  min={0}
                  step="0.01"
                  onChange={(event) => setPrice(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanPeriodPopup.input.price"
                />

                {isEditing && (
                  <SimpleToggle
                    value={isActive}
                    title={t("admin_dashboard.plans_and_coupons.period_is_active")}
                    onChange={setIsActive}
                    descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanPeriodPopup.toggle.isActive"
                  />
                )}
              </div>

              <div className="flex flex-col-reverse gap-2 p-2 md:flex-row md:justify-end">
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                  onClick={onClose}
                  data-interaction-description-key="interactionDescriptions:pages.adminDashboard.adminPlanPeriodPopup.button.cancel"
                >
                  {t("admin_dashboard.plans_and_coupons.cancel")}
                </button>

                <button
                  type="submit"
                  className="rounded-lg border border-orange-500 bg-orange-600 px-4 py-2 text-sm font-bold text-white hover:bg-orange-700"
                  data-interaction-description-key="interactionDescriptions:pages.adminDashboard.adminPlanPeriodPopup.button.save"
                >
                  {t("admin_dashboard.plans_and_coupons.save")}
                </button>
              </div>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
